import { Controller } from "@hotwired/stimulus";
import Quill from 'quill';

const MAX_IMAGE_DIMENSION = 1600;
const JPEG_QUALITY = 0.8;
const ENCODED_IMAGE_PREFIX = 'data:image/';

// Rails' sanitizer keeps only these media types on an <img src>, so anything else pasted as a
// data URI reaches the ticket as an image with no source at all.
const EMBEDDABLE_IMAGE_PREFIXES = ['data:image/jpeg', 'data:image/png', 'data:image/gif'];
const LOSSLESSLY_EMBEDDABLE_TYPES = ['image/png', 'image/gif'];

// Everything else the sanitizer leaves with its source intact: a blob: or file: source survives
// the paste into the editor but reaches the ticket stripped, so it has to be rejected here.
const LINKABLE_IMAGE_SOURCE = /^(https?:)?\/\//i;

const Delta = Quill.import('delta');

const FORMATS_THE_TICKET_KEEPS = [
    'bold', 'italic', 'underline', 'strike', 'link', 'image',
    'list', 'indent', 'align', 'header', 'blockquote', 'code-block'
];

// Quill's uploader drops any file whose type is outside this list before the handler runs, so
// anything missing here is discarded on drop and paste without reaching the budget or the user.
// Browsers report no type at all for image formats the OS does not know, such as HEIC on Windows.
const UNREPORTED_TYPE = '';
const DROPPABLE_IMAGE_TYPES = [
    'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif',
    'image/bmp', 'image/tiff', 'image/heic', 'image/heif', 'image/svg+xml', UNREPORTED_TYPE
];

export default class extends Controller {
    static targets = ['editor', 'hiddenInput', 'error'];
    static values = {
        maxContentSize: Number,
        oversizedImageMessage: String,
        unsupportedImageMessage: String,
        pastedImageName: String
    };

    connect() {
        if (!this.hasErrorTarget) {
            throw new Error('quill-editor needs an error target to report images it could not embed');
        }

        this.pastedUnsupported = [];
        this.pastedOversized = [];
        this.pastedLength = 0;
        this.pastedSettlementPending = false;

        this.quill = new Quill(this.editorTarget, {
            theme: 'snow',
            formats: FORMATS_THE_TICKET_KEEPS,
            modules: {
                toolbar: {
                    container: [
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'indent': '-1'}, { 'indent': '+1' }],
                        [{ 'align': [] }],
                        ['link', 'image'],
                        ['clean']
                    ],
                    handlers: { image: () => this.pickImage() }
                },
                uploader: {
                    mimetypes: DROPPABLE_IMAGE_TYPES,
                    handler: (range, files) => this.insertImages(range, files)
                }
            },
        });

        this.quill.clipboard.addMatcher('IMG', (node, delta) => this.embeddablePastedImage(node, delta));

        this.quill.on('text-change', (delta, previous, source) => {
            this.hiddenInputTarget.value = this.quill.root.innerHTML;

            if (source === 'user') this.showRejection('');
        });
    }

    disconnect() {
        clearTimeout(this.pastedSettlementTimeout);
        this.pastedSettlementPending = false;
    }

    pickImage() {
        const picker = document.createElement('input');
        picker.type = 'file';
        picker.accept = 'image/*';
        picker.addEventListener('change', () => {
            this.insertImages(this.quill.getSelection(true), Array.from(picker.files));
        });
        picker.click();
    }

    async insertImages(range, files) {
        this.showRejection('');

        const images = Array.from(files).filter((file) => this.mayBeImage(file));
        if (images.length === 0) return;

        let index = range.index;
        const unreadable = [];
        const oversized = [];

        if (range.length) this.quill.deleteText(index, range.length, 'user');

        const followEdits = (delta) => { index = delta.transformPosition(index); };
        this.quill.on('text-change', followEdits);
        const candidates = await Promise.all(images.map((image) => this.embeddableSources(image)));
        this.quill.off('text-change', followEdits);

        images.forEach((image, position) => {
            const sources = candidates[position];
            const source = sources.find((candidate) => this.fitsWithinBudget(candidate));

            if (sources.length === 0) {
                unreadable.push(image.name);
            } else if (!source) {
                oversized.push(image.name);
            } else {
                this.quill.insertEmbed(index, 'image', source, 'user');
                index += 1;
            }
        });

        this.reportRejections(unreadable, oversized);
    }

    mayBeImage(file) {
        return file.type === UNREPORTED_TYPE || file.type.startsWith('image/');
    }

    embeddablePastedImage(node, delta) {
        const source = node.getAttribute('src') || '';

        if (LINKABLE_IMAGE_SOURCE.test(source)) return delta;

        const name = node.getAttribute('alt') || this.pastedImageNameValue;
        let embeddable = false;

        if (!EMBEDDABLE_IMAGE_PREFIXES.some((prefix) => source.startsWith(prefix))) {
            this.pastedUnsupported.push(name);
        } else if (!this.fitsWithinBudget(source, this.pastedLength)) {
            this.pastedOversized.push(name);
        } else {
            this.pastedLength += source.length;
            embeddable = true;
        }

        this.settlePaste();

        return embeddable ? delta : new Delta();
    }

    // Quill applies the paste, clearing any shown rejection, only after every matcher has run.
    settlePaste() {
        if (this.pastedSettlementPending) return;

        this.pastedSettlementPending = true;
        this.pastedSettlementTimeout = setTimeout(() => {
            if (this.pastedUnsupported.length > 0 || this.pastedOversized.length > 0) {
                this.reportRejections(this.pastedUnsupported, this.pastedOversized);
            }

            this.pastedUnsupported = [];
            this.pastedOversized = [];
            this.pastedLength = 0;
            this.pastedSettlementPending = false;
        });
    }

    async embeddableSources(file) {
        const sources = await Promise.all([
            LOSSLESSLY_EMBEDDABLE_TYPES.includes(file.type) ? this.original(file) : null,
            this.shrink(file)
        ]);

        return sources.filter(Boolean);
    }

    original(file) {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(this.encodedImageOrNull(reader.result));
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
        });
    }

    async shrink(file) {
        let bitmap;

        try {
            bitmap = await createImageBitmap(file);
            const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(bitmap.width, bitmap.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(bitmap.width * scale));
            canvas.height = Math.max(1, Math.round(bitmap.height * scale));

            const context = canvas.getContext('2d');
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

            return this.encodedImageOrNull(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
        } catch (error) {
            console.error('quill-editor could not encode an image', file.name, file.type, file.size, error);

            return null;
        } finally {
            bitmap?.close();
        }
    }

    // A canvas the browser declines to encode yields the string "data:," instead of throwing.
    encodedImageOrNull(source) {
        return source?.startsWith(ENCODED_IMAGE_PREFIX) ? source : null;
    }

    fitsWithinBudget(source, claimedByEarlierPastedImages = 0) {
        return this.hiddenInputTarget.value.length + claimedByEarlierPastedImages + source.length <= this.maxContentSizeValue;
    }

    reportRejections(unreadable, oversized) {
        const rejections = [
            [unreadable, this.unsupportedImageMessageValue],
            [oversized, this.oversizedImageMessageValue]
        ];

        this.showRejection(rejections
            .filter(([names]) => names.length > 0)
            .map(([names, reason]) => `${names.join(', ')}: ${reason}`)
            .join(' '));
    }

    showRejection(message) {
        this.errorTarget.textContent = message;
    }
}
